import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Copy, Check, ChevronDown, ChevronUp, Eye, EyeOff, Shield, Building2, Users, Plus, X, Pencil, Trash2, ExternalLink, FileText, AlertTriangle } from 'lucide-react';

// ─── Colour system ────────────────────────────────────────────────────────────
const C = {
  cream: '#F7F6EF', charcoal: '#2F3A42', navy: '#445366',
  mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  green: '#7EA594', greenLight: '#EAF2EF',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  red: '#DC3545', redLight: '#FFF0F0',
  white: '#FFFFFF', yellow: '#F7EC71',
};

// ─── Role helpers ─────────────────────────────────────────────────────────────
const ROLE_RANK = { bar: 0, kitchen: 0, supervisor: 1, manager: 2, admin: 3 };
const atLeast = (role, min) => (ROLE_RANK[role] ?? 0) >= (ROLE_RANK[min] ?? 99);

// ─── Hardcoded entity data ────────────────────────────────────────────────────
const ENTITIES = [
  {
    id: 'lekkermate',
    bv: 'Lekker Mate B.V.',
    shortName: 'Lekker Mate',
    accent: C.skyText,
    accentLight: C.skyBlueLight,
    accentBorder: C.skyBlue,
    fields: [
      { label: 'KvK',              value: '94652147' },
      { label: 'BTW (VAT)',        value: 'NL866848885B01' },
      { label: 'Bank IBAN',        value: 'NL65REVO6752741719' },
      { label: 'SWIFT/BIC',        value: 'REVONL22' },
      { label: 'Reg. Address',     value: 'Edisonstraat 38 1, 1098TD Amsterdam' },
      { label: 'Delivery (Sassys)', value: 'Schaafstraat 21, 1021 KD, Amsterdam' },
      { label: 'Delivery (Prep)',   value: 'Hoofdweg 236-H, 1057 DG, Amsterdam' },
      { label: 'Invoice Email',    value: 'lekkermate.c150@app.hubdoc.com', isEmail: true },
    ],
  },
  {
    id: 'jordaan',
    bv: 'FOBS B.V.',
    shortName: 'Box Jordaan',
    accent: '#2563EB',
    accentLight: '#EDF3FC',
    accentBorder: '#BAD4F5',
    fields: [
      { label: 'KvK',          value: '88496899' },
      { label: 'BTW (VAT)',    value: 'NL864653438B01' },
      { label: 'Bank IBAN',    value: 'NL75 RABO 0321 5995 86' },
      { label: 'SWIFT/BIC',   value: 'RABONL2U' },
      { label: 'Reg. Address', value: 'Bloemgracht 47, 1016 KD, Nederland' },
      { label: 'Invoice Email', value: 'fobs.3637@to-zenvoices.com', isEmail: true },
    ],
  },
  {
    id: 'plantage',
    bv: 'Box Social B.V.',
    shortName: 'Box Plantage',
    accent: '#2D7A65',
    accentLight: '#C8E6D8',
    accentBorder: '#7EA594',
    fields: [
      { label: 'KvK',          value: '70252297' },
      { label: 'BTW (VAT)',    value: 'NL858215883B01' },
      { label: 'Bank IBAN',    value: 'NL25 RABO 0325 2731 97' },
      { label: 'SWIFT/BIC',   value: 'RABONL2U' },
      { label: 'Reg. Address', value: 'Plantage Middenlaan 30a, 1018 DG, Nederland' },
      { label: 'Invoice Email', value: 'boxsocial.3637@to-zenvoices.com', isEmail: true },
    ],
  },
];

// ─── Copy hook ────────────────────────────────────────────────────────────────
function useCopy() {
  const [copied, setCopied] = useState(null);
  const copy = (val, id) => {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    });
  };
  return { copied, copy };
}

// ─── CopyField ────────────────────────────────────────────────────────────────
function CopyField({ label, value, id, copied, onCopy }) {
  const isCopied = copied === id;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: 600, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>{label}</div>
        <div style={{ fontSize: '13px', color: C.charcoal, fontWeight: 500, wordBreak: 'break-all' }}>{value}</div>
      </div>
      <button
        onClick={() => onCopy(value, id)}
        title="Copy"
        style={{
          marginLeft: '10px', flexShrink: 0,
          width: '28px', height: '28px', borderRadius: '6px',
          border: `1px solid ${isCopied ? C.green : C.border}`,
          background: isCopied ? C.greenLight : C.white,
          color: isCopied ? C.green : C.mid,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}
      >
        {isCopied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}

// ─── EntityCard ───────────────────────────────────────────────────────────────
function EntityCard({ entity, copied, onCopy }) {
  const [open, setOpen] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  function copyAll() {
    const text = entity.fields.map(f => `${f.label}: ${f.value}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1800);
    });
  }

  return (
    <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${entity.accentBorder}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      {/* Header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: entity.accentLight, cursor: 'pointer', userSelect: 'none' }}
      >
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: entity.accent }}>{entity.bv}</div>
          <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px' }}>{entity.shortName}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {open && (
            <button
              onClick={e => { e.stopPropagation(); copyAll(); }}
              title="Copy all details"
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                border: `1px solid ${copiedAll ? C.green : entity.accentBorder}`,
                background: copiedAll ? C.greenLight : C.white,
                color: copiedAll ? C.green : entity.accent,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {copiedAll ? <Check size={11} /> : <Copy size={11} />}
              {copiedAll ? 'Copied!' : 'Copy all'}
            </button>
          )}
          {open ? <ChevronUp size={16} color={C.mid} /> : <ChevronDown size={16} color={C.mid} />}
        </div>
      </div>

      {/* Fields */}
      {open && (
        <div style={{ padding: '4px 18px 10px' }}>
          {entity.fields.map((f, i) => (
            <CopyField
              key={i}
              label={f.label}
              value={f.value}
              id={`${entity.id}-${i}`}
              copied={copied}
              onCopy={onCopy}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SupplierCard ─────────────────────────────────────────────────────────────
function SupplierCard({ supplier, copied, onCopy, canEdit, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const hasDetails = supplier.contact_name || supplier.contact_phone || supplier.contact_email || supplier.order_portal_url || supplier.account_number || supplier.notes;

  return (
    <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
      <div
        onClick={() => hasDetails && setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: hasDetails ? 'pointer' : 'default', background: C.white }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.charcoal }}>{supplier.name}</div>
          {supplier.contact_name && <div style={{ fontSize: '12px', color: C.mid, marginTop: '1px' }}>{supplier.contact_name}</div>}
        </div>
        <span style={{
          fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', flexShrink: 0,
          background: supplier.type === 'internal' ? C.skyBlueLight : C.orangeLight,
          color: supplier.type === 'internal' ? C.skyText : C.orange,
        }}>
          {supplier.type === 'internal' ? 'Internal' : 'External'}
        </span>
        {canEdit && (
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => onEdit(supplier)} style={{ padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Pencil size={11} /> Edit
            </button>
          </div>
        )}
        {hasDetails && (open ? <ChevronUp size={14} color={C.mid} /> : <ChevronDown size={14} color={C.mid} />)}
      </div>

      {open && hasDetails && (
        <div style={{ padding: '2px 16px 12px', borderTop: `1px solid ${C.border}`, background: '#FAFAFA' }}>
          {[
            { label: 'Phone', value: supplier.contact_phone },
            { label: 'Email', value: supplier.contact_email },
            { label: 'Account No.', value: supplier.account_number },
            { label: 'Order Portal', value: supplier.order_portal_url },
          ].filter(f => f.value).map((f, i) => (
            <CopyField key={i} label={f.label} value={f.value} id={`supp-${supplier.id}-${i}`} copied={copied} onCopy={onCopy} />
          ))}
          {supplier.notes && (
            <div style={{ marginTop: '8px', padding: '8px 10px', background: C.orangeLight, borderRadius: '6px', fontSize: '12px', color: C.charcoal }}>
              <span style={{ fontWeight: 600, color: C.orange }}>Note: </span>{supplier.notes}
            </div>
          )}
          {supplier.order_portal_url && (
            <a href={supplier.order_portal_url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px', fontSize: '12px', color: C.skyText, textDecoration: 'none', fontWeight: 500 }}>
              <ExternalLink size={11} /> Open portal
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SupplierModal ────────────────────────────────────────────────────────────
function SupplierModal({ supplier, onClose, onSave }) {
  const [form, setForm] = useState({
    contact_name: supplier?.contact_name || '',
    contact_phone: supplier?.contact_phone || '',
    contact_email: supplier?.contact_email || '',
    order_portal_url: supplier?.order_portal_url || '',
    account_number: supplier?.account_number || '',
    notes: supplier?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('suppliers').update(form).eq('id', supplier.id);
    setSaving(false);
    if (!error) onSave({ ...supplier, ...form });
  };

  const inp = { padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '13px', color: C.charcoal, width: '100%', boxSizing: 'border-box', outline: 'none' };
  const lbl = { fontSize: '11px', fontWeight: 600, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '14px', padding: '24px', width: '440px', maxWidth: '95%', boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal }}>Edit Contact Details</div>
            <div style={{ fontSize: '12px', color: C.mid, marginTop: '2px' }}>{supplier.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            { key: 'contact_name', label: 'Contact Name', placeholder: 'e.g. Jan de Vries' },
            { key: 'contact_phone', label: 'Phone', placeholder: '+31 6 12345678' },
            { key: 'contact_email', label: 'Email', placeholder: 'orders@supplier.nl' },
            { key: 'account_number', label: 'Account / Customer No.', placeholder: 'e.g. ACC-00123' },
            { key: 'order_portal_url', label: 'Order Portal URL', placeholder: 'https://...' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label style={lbl}>{label}</label>
              <input value={form[key]} onChange={set(key)} placeholder={placeholder} style={inp} />
            </div>
          ))}
          <div>
            <label style={lbl}>Notes</label>
            <textarea value={form.notes} onChange={set('notes')} placeholder="Order cut-off times, special instructions..." rows={3}
              style={{ ...inp, resize: 'vertical' }} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', background: C.navy, color: C.white, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CredentialRow ────────────────────────────────────────────────────────────
function CredentialRow({ cred, copied, onCopy, canEdit, onEdit, onDelete }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, background: C.white }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>{cred.label}</div>
        {cred.category && <div style={{ fontSize: '11px', color: C.mid }}>{cred.category}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
          {cred.username && (
            <div style={{ fontSize: '12px', color: C.navy }}>
              <span style={{ color: C.mid, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>User </span>
              {cred.username}
            </div>
          )}
          {cred.password && (
            <div style={{ fontSize: '12px', color: C.navy, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: C.mid, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pass </span>
              <span style={{ fontFamily: 'monospace', letterSpacing: revealed ? '0.05em' : '0.1em' }}>
                {revealed ? cred.password : '••••••••'}
              </span>
              <button onClick={() => setRevealed(r => !r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0 2px', display: 'flex', alignItems: 'center' }}>
                {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
              {revealed && (
                <button onClick={() => onCopy(cred.password, `cred-pw-${cred.id}`)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied === `cred-pw-${cred.id}` ? C.green : C.mid, padding: '0 2px', display: 'flex', alignItems: 'center' }}>
                  {copied === `cred-pw-${cred.id}` ? <Check size={12} /> : <Copy size={12} />}
                </button>
              )}
            </div>
          )}
        </div>
        {cred.notes && <div style={{ fontSize: '11px', color: C.mid, marginTop: '3px', fontStyle: 'italic' }}>{cred.notes}</div>}
      </div>
      {cred.url && (
        <a href={cred.url} target="_blank" rel="noopener noreferrer"
          style={{ color: C.skyText, display: 'flex', alignItems: 'center' }}>
          <ExternalLink size={14} />
        </a>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <button onClick={() => onEdit(cred)} style={{ padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <Pencil size={11} />
          </button>
          <button onClick={() => onDelete(cred)} style={{ padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.redLight}`, background: C.redLight, color: C.red, cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center' }}>
            <Trash2 size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CredentialModal ──────────────────────────────────────────────────────────
function CredentialModal({ cred, onClose, onSave }) {
  const [form, setForm] = useState({
    label: cred?.label || '',
    category: cred?.category || '',
    username: cred?.username || '',
    password: cred?.password || '',
    url: cred?.url || '',
    notes: cred?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.label.trim()) return;
    setSaving(true);
    if (cred?.id) {
      const { error } = await supabase.from('credentials').update(form).eq('id', cred.id);
      if (!error) onSave({ ...cred, ...form });
    } else {
      const { data, error } = await supabase.from('credentials').insert(form).select().single();
      if (!error) onSave(data);
    }
    setSaving(false);
  };

  const inp = { padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '13px', color: C.charcoal, width: '100%', boxSizing: 'border-box', outline: 'none' };
  const lbl = { fontSize: '11px', fontWeight: 600, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' };

  const CATEGORIES = ['Google', 'Supplier Portals', 'Banking', 'WiFi', 'POS', 'Other'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '14px', padding: '24px', width: '420px', maxWidth: '95%', boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal }}>{cred?.id ? 'Edit Credential' : 'Add Credential'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={lbl}>Label *</label>
            <input value={form.label} onChange={set('label')} placeholder="e.g. Google Workspace" style={inp} />
          </div>
          <div>
            <label style={lbl}>Category</label>
            <select value={form.category} onChange={set('category')} style={inp}>
              <option value="">— select —</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={lbl}>Username / Email</label>
              <input value={form.username} onChange={set('username')} placeholder="username" style={inp} />
            </div>
            <div>
              <label style={lbl}>Password</label>
              <input value={form.password} onChange={set('password')} placeholder="password" style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>URL</label>
            <input value={form.url} onChange={set('url')} placeholder="https://..." style={inp} />
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <input value={form.notes} onChange={set('notes')} placeholder="e.g. shared team account" style={inp} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.label.trim()} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', background: C.navy, color: C.white, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px', opacity: (saving || !form.label.trim()) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : cred?.id ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── License helpers ──────────────────────────────────────────────────────────
function licenseStatus(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const exp   = new Date(expiryDate + 'T00:00:00');
  const days  = Math.round((exp - today) / 86400000);
  if (days < 0)   return { label: 'Expired',    color: C.red,    bg: C.redLight,    days, urgent: true  };
  if (days <= 30) return { label: `${days}d`,   color: C.red,    bg: C.redLight,    days, urgent: true  };
  if (days <= 60) return { label: `${days}d`,   color: C.orange, bg: C.orangeLight, days, urgent: false };
  return             { label: `${days}d`,        color: C.green,  bg: C.greenLight,  days, urgent: false };
}

function formatDateNL(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── LicenseModal ─────────────────────────────────────────────────────────────
function LicenseModal({ license, onClose, onSave }) {
  const [form, setForm] = useState({
    name:           license?.name           || '',
    venue:          license?.venue          || '',
    entity:         license?.entity         || '',
    license_number: license?.license_number || '',
    issued_date:    license?.issued_date    || '',
    expiry_date:    license?.expiry_date    || '',
    notes:          license?.notes          || '',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.expiry_date) return;
    setSaving(true);
    const payload = { ...form, name: form.name.trim(), notes: form.notes.trim() || null };
    if (license?.id) {
      const { data } = await supabase.from('licenses').update(payload).eq('id', license.id).select().single();
      if (data) onSave(data);
    } else {
      const { data } = await supabase.from('licenses').insert(payload).select().single();
      if (data) onSave(data);
    }
    setSaving(false);
  };

  const inp = { padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '13px', color: C.charcoal, width: '100%', boxSizing: 'border-box', outline: 'none' };
  const lbl = { fontSize: '11px', fontWeight: 600, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' };

  const VENUES   = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen', 'All Venues'];
  const ENTITIES = ['Lekker Mate B.V.', 'FOBS B.V.', 'Box Social B.V.'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '14px', padding: '24px', width: '460px', maxWidth: '95%', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal }}>{license?.id ? 'Edit License' : 'Add License'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={lbl}>License Name *</label>
            <input value={form.name} onChange={set('name')} placeholder="e.g. Horecavergunning" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={lbl}>Venue</label>
              <select value={form.venue} onChange={set('venue')} style={inp}>
                <option value="">— select —</option>
                {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Entity (BV)</label>
              <select value={form.entity} onChange={set('entity')} style={inp}>
                <option value="">— select —</option>
                {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={lbl}>License Number</label>
            <input value={form.license_number} onChange={set('license_number')} placeholder="e.g. HV-2024-00123" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={lbl}>Issue Date</label>
              <input type="date" value={form.issued_date} onChange={set('issued_date')} style={inp} />
            </div>
            <div>
              <label style={lbl}>Expiry Date *</label>
              <input type="date" value={form.expiry_date} onChange={set('expiry_date')} style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <textarea value={form.notes} onChange={set('notes')} placeholder="Renewal contact, reference number, notes..." rows={2}
              style={{ ...inp, resize: 'vertical' }} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.expiry_date}
            style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', background: C.navy, color: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '13px', opacity: (saving || !form.name.trim() || !form.expiry_date) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : license?.id ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function DocsPage({ user, userRole }) {
  const isManagerPlus = atLeast(userRole, 'manager');
  const isAdmin       = atLeast(userRole, 'admin');

  const [activeTab, setActiveTab]       = useState('entities');
  const [suppliers, setSuppliers]       = useState([]);
  const [suppLoading, setSuppLoading]   = useState(true);
  const [suppSearch, setSuppSearch]     = useState('');
  const [suppFilter, setSuppFilter]     = useState('all'); // 'all' | 'external' | 'internal'
  const [editingSupp, setEditingSupp]   = useState(null);

  const [credentials, setCredentials]   = useState([]);
  const [credLoading, setCredLoading]   = useState(true);
  const [credSearch, setCredSearch]     = useState('');
  const [credCategory, setCredCategory] = useState('all');
  const [editingCred, setEditingCred]   = useState(null);
  const [addingCred, setAddingCred]     = useState(false);
  const [deleteCredId, setDeleteCredId] = useState(null);

  const [licenses, setLicenses]         = useState([]);
  const [licLoading, setLicLoading]     = useState(true);
  const [editingLic, setEditingLic]     = useState(null);
  const [addingLic, setAddingLic]       = useState(false);
  const [deleteLicId, setDeleteLicId]   = useState(null);

  const { copied, copy } = useCopy();

  const loadSuppliers = useCallback(async () => {
    setSuppLoading(true);
    const { data } = await supabase.from('suppliers').select('*').eq('active', true).order('name');
    if (data) setSuppliers(data);
    setSuppLoading(false);
  }, []);

  const loadCredentials = useCallback(async () => {
    setCredLoading(true);
    const { data } = await supabase.from('credentials').select('*').order('category').order('label');
    if (data) setCredentials(data);
    setCredLoading(false);
  }, []);

  const loadLicenses = useCallback(async () => {
    setLicLoading(true);
    const { data } = await supabase.from('licenses').select('*').order('expiry_date');
    if (data) setLicenses(data);
    setLicLoading(false);
  }, []);

  useEffect(() => {
    if (isManagerPlus) {
      loadSuppliers();
      loadCredentials();
      loadLicenses();
    }
  }, [isManagerPlus, loadSuppliers, loadCredentials, loadLicenses]);

  // ── Gate ───────────────────────────────────────────────────────────────────
  if (!isManagerPlus) {
    return (
      <div style={{ padding: '60px 24px', textAlign: 'center' }}>
        <Shield size={32} color={C.mid} style={{ marginBottom: '12px' }} />
        <div style={{ fontSize: '15px', fontWeight: 600, color: C.charcoal }}>Managers &amp; above only</div>
        <div style={{ fontSize: '13px', color: C.mid, marginTop: '4px' }}>Contact your manager if you need access to this information.</div>
      </div>
    );
  }

  // ── Filtered suppliers ─────────────────────────────────────────────────────
  const filteredSuppliers = suppliers.filter(s => {
    const matchSearch = !suppSearch || s.name.toLowerCase().includes(suppSearch.toLowerCase())
      || (s.contact_name || '').toLowerCase().includes(suppSearch.toLowerCase());
    const matchType = suppFilter === 'all' || s.type === suppFilter;
    return matchSearch && matchType;
  });

  // ── Filtered credentials ───────────────────────────────────────────────────
  const credCategories = ['all', ...Array.from(new Set(credentials.map(c => c.category).filter(Boolean)))];
  const filteredCreds = credentials.filter(c => {
    const matchSearch = !credSearch || c.label.toLowerCase().includes(credSearch.toLowerCase())
      || (c.category || '').toLowerCase().includes(credSearch.toLowerCase());
    const matchCat = credCategory === 'all' || c.category === credCategory;
    return matchSearch && matchCat;
  });

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'entities',     label: 'Entity Info',    icon: Building2 },
    { id: 'suppliers',    label: 'Suppliers',       icon: Users },
    { id: 'credentials',  label: 'Credentials',     icon: Shield },
    { id: 'licenses',     label: 'Licenses',        icon: FileText },
  ];

  const tabBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '7px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: active ? 600 : 400,
    background: active ? C.charcoal : 'transparent',
    color: active ? C.white : C.mid,
    transition: 'all 0.15s',
  });

  const filterPill = (active) => ({
    padding: '4px 12px', borderRadius: '20px', border: `1px solid ${active ? C.charcoal : C.border}`,
    background: active ? C.charcoal : C.white, color: active ? C.white : C.mid,
    fontSize: '12px', fontWeight: active ? 600 : 400, cursor: 'pointer',
  });

  const searchInput = (val, setter, placeholder) => (
    <input
      value={val}
      onChange={e => setter(e.target.value)}
      placeholder={placeholder}
      style={{ padding: '7px 12px', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.charcoal, outline: 'none', width: '200px' }}
    />
  );

  return (
    <div style={{ padding: '24px', maxWidth: '960px', fontFamily: 'inherit' }}>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.charcoal }}>Info Hub</h2>
        <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.mid }}>Business details, supplier contacts &amp; credentials — manager access only</p>
      </div>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: C.cream, padding: '4px', borderRadius: '24px', width: 'fit-content' }}>
        {TABS.map(({ id, label, icon: Icon }) => {
          const isActive = activeTab === id;
          const urgentLicenses = id === 'licenses' ? licenses.filter(l => { const s = licenseStatus(l.expiry_date); return s && s.days <= 60; }).length : 0;
          return (
            <button key={id} onClick={() => setActiveTab(id)} style={{ ...tabBtn(isActive), position: 'relative' }}>
              <Icon size={14} /> {label}
              {urgentLicenses > 0 && (
                <span style={{ position: 'absolute', top: '2px', right: '2px', width: '8px', height: '8px', borderRadius: '50%', background: C.red, border: `2px solid ${C.cream}` }} />
              )}
            </button>
          );
        })}
      </div>
      {/* ── ENTITY INFO ─────────────────────────────────────────────────────── */}
      {activeTab === 'entities' && (
        <div>
          <div style={{ fontSize: '12px', color: C.mid, marginBottom: '16px' }}>
            Click any field's copy button to copy to clipboard.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {ENTITIES.map(entity => (
              <EntityCard key={entity.id} entity={entity} copied={copied} onCopy={copy} />
            ))}
          </div>
        </div>
      )}

      {/* ── SUPPLIERS ───────────────────────────────────────────────────────── */}
      {activeTab === 'suppliers' && (
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            {searchInput(suppSearch, setSuppSearch, 'Search suppliers…')}
            <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
              {['all', 'external', 'internal'].map(f => (
                <button key={f} onClick={() => setSuppFilter(f)} style={filterPill(suppFilter === f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {suppLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.mid }}>Loading…</div>
          ) : filteredSuppliers.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.mid, fontSize: '13px' }}>No suppliers match your search.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredSuppliers.map(s => (
                <SupplierCard
                  key={s.id}
                  supplier={s}
                  copied={copied}
                  onCopy={copy}
                  canEdit={isAdmin}
                  onEdit={setEditingSupp}
                />
              ))}
            </div>
          )}
          <div style={{ marginTop: '16px', fontSize: '12px', color: C.mid }}>
            Showing {filteredSuppliers.length} of {suppliers.length} active suppliers. Manage suppliers &amp; categories in <strong>Settings</strong>.
          </div>
        </div>
      )}

      {/* ── CREDENTIALS ─────────────────────────────────────────────────────── */}
      {activeTab === 'credentials' && (
        <div>
          {/* Gated notice */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: C.orangeLight, borderRadius: '8px', marginBottom: '16px', fontSize: '12px', color: C.charcoal }}>
            <Shield size={14} color={C.orange} />
            Visible to managers and admins only. Do not share passwords outside this system.
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            {searchInput(credSearch, setCredSearch, 'Search credentials…')}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {credCategories.map(cat => (
                <button key={cat} onClick={() => setCredCategory(cat)} style={filterPill(credCategory === cat)}>
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
            {isAdmin && (
              <button onClick={() => setAddingCred(true)}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px', borderRadius: '8px', border: 'none', background: C.navy, color: C.white, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                <Plus size={14} /> Add
              </button>
            )}
          </div>

          {credLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.mid }}>Loading…</div>
          ) : filteredCreds.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.mid, fontSize: '13px' }}>
              {credentials.length === 0 ? (isAdmin ? 'No credentials yet — add one above.' : 'No credentials added yet.') : 'No matches.'}
            </div>
          ) : (
            <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              {filteredCreds.map((cred, i) => (
                <CredentialRow
                  key={cred.id}
                  cred={cred}
                  copied={copied}
                  onCopy={copy}
                  canEdit={isAdmin}
                  onEdit={setEditingCred}
                  onDelete={(c) => setDeleteCredId(c.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── LICENSES ────────────────────────────────────────────────────────── */}
      {activeTab === 'licenses' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ fontSize: '12px', color: C.mid }}>
              Track license expiry dates. Red = &lt;30 days or expired. Amber = 30–60 days.
            </div>
            {isAdmin && (
              <button onClick={() => setAddingLic(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px', borderRadius: '8px', border: 'none', background: C.navy, color: C.white, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                <Plus size={14} /> Add License
              </button>
            )}
          </div>

          {licLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.mid }}>Loading…</div>
          ) : licenses.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.mid, fontSize: '13px' }}>
              {isAdmin ? 'No licenses yet — add one above.' : 'No licenses added yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {licenses.map(lic => {
                const status = licenseStatus(lic.expiry_date);
                return (
                  <div key={lic.id} style={{ background: C.white, borderRadius: '10px', border: `1px solid ${status?.urgent ? C.red + '55' : C.border}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {/* Status indicator */}
                    <div style={{ width: '4px', alignSelf: 'stretch', borderRadius: '4px', background: status ? status.color : C.border, flexShrink: 0 }} />

                    {/* Main info */}
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: C.charcoal }}>{lic.name}</div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '3px' }}>
                        {lic.venue && <span style={{ fontSize: '11px', color: C.mid }}>{lic.venue}</span>}
                        {lic.entity && <span style={{ fontSize: '11px', color: C.mid }}>· {lic.entity}</span>}
                        {lic.license_number && <span style={{ fontSize: '11px', color: C.mid }}>· {lic.license_number}</span>}
                      </div>
                      {lic.notes && <div style={{ fontSize: '11px', color: C.mid, marginTop: '3px', fontStyle: 'italic' }}>{lic.notes}</div>}
                    </div>

                    {/* Dates */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '11px', color: C.mid, marginBottom: '3px' }}>
                        {lic.issued_date ? `Issued ${formatDateNL(lic.issued_date)}` : ''}
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: C.charcoal }}>
                        Expires {formatDateNL(lic.expiry_date)}
                      </div>
                    </div>

                    {/* Status pill */}
                    {status && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', background: status.bg, flexShrink: 0 }}>
                        {status.urgent && <AlertTriangle size={11} color={status.color} />}
                        <span style={{ fontSize: '11px', fontWeight: 700, color: status.color }}>
                          {status.days < 0 ? 'Expired' : `${status.days}d left`}
                        </span>
                      </div>
                    )}

                    {/* Admin actions */}
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <button onClick={() => setEditingLic(lic)}
                          style={{ padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Pencil size={11} /> Edit
                        </button>
                        <button onClick={() => setDeleteLicId(lic.id)}
                          style={{ padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.redLight}`, background: C.redLight, color: C.red, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {editingSupp && (
        <SupplierModal
          supplier={editingSupp}
          onClose={() => setEditingSupp(null)}
          onSave={(updated) => {
            setSuppliers(prev => prev.map(s => s.id === updated.id ? updated : s));
            setEditingSupp(null);
          }}
        />
      )}

      {(addingCred || editingCred) && (
        <CredentialModal
          cred={editingCred}
          onClose={() => { setAddingCred(false); setEditingCred(null); }}
          onSave={(saved) => {
            if (editingCred) {
              setCredentials(prev => prev.map(c => c.id === saved.id ? saved : c));
            } else {
              setCredentials(prev => [...prev, saved]);
            }
            setAddingCred(false);
            setEditingCred(null);
          }}
        />
      )}

      {deleteCredId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDeleteCredId(null)}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '340px', maxWidth: '95%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.charcoal, marginBottom: '8px' }}>Delete credential?</div>
            <div style={{ fontSize: '13px', color: C.mid, marginBottom: '20px' }}>This cannot be undone.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setDeleteCredId(null)} style={{ padding: '7px 14px', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              <button onClick={async () => {
                await supabase.from('credentials').delete().eq('id', deleteCredId);
                setCredentials(prev => prev.filter(c => c.id !== deleteCredId));
                setDeleteCredId(null);
              }} style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', background: C.red, color: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {(addingLic || editingLic) && (
        <LicenseModal
          license={editingLic}
          onClose={() => { setAddingLic(false); setEditingLic(null); }}
          onSave={(saved) => {
            if (editingLic) {
              setLicenses(prev => prev.map(l => l.id === saved.id ? saved : l).sort((a,b) => a.expiry_date.localeCompare(b.expiry_date)));
            } else {
              setLicenses(prev => [...prev, saved].sort((a,b) => a.expiry_date.localeCompare(b.expiry_date)));
            }
            setAddingLic(false);
            setEditingLic(null);
          }}
        />
      )}

      {deleteLicId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDeleteLicId(null)}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '340px', maxWidth: '95%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.charcoal, marginBottom: '8px' }}>Delete license?</div>
            <div style={{ fontSize: '13px', color: C.mid, marginBottom: '20px' }}>This cannot be undone.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setDeleteLicId(null)} style={{ padding: '7px 14px', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              <button onClick={async () => {
                await supabase.from('licenses').delete().eq('id', deleteLicId);
                setLicenses(prev => prev.filter(l => l.id !== deleteLicId));
                setDeleteLicId(null);
              }} style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', background: C.red, color: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}